import _ from 'lodash';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { GetBasicByIdDTO } from '../../services/patent-repository';
import { GetPatentLinkDTO } from '../get-patent-link';
import { GetPatentValuationDTO } from '../get-patent-valuation';
import { GetPatentTransferDTO } from '../get-patent-transfer';
import { GetPatentJpdcDTO } from '../get-patent-jpdc';
import { GetPatentBasic, type ApiGetPatentBasicResponse } from '@/infrastructure/api-functions/core-search-api/get-patent-basic-api';
import { GetPatentInitTab } from '@/infrastructure/api-functions/core-search-api/get-patent-init-tab-api';
import { OVER_VIEW_LIMIT_ERROR_MESSAGE, DEFAULT_PATENT_BASIC_DATA } from '../../constants';
import { getFieldValueByLang } from '../../utils/get-field-value-by-lang';
import { GetFullParamsService } from '@/services/common/get-full-params.interface';
import { PsHttpError } from '@patsnap/tahiti-core/common/ps-http';
import type { PatentBasicEntity, PatentOriginExtend, PatentDataStatusExtend } from '../../types';

type SubsetApiGetPatentBasicResponse = Pick<ApiGetPatentBasicResponse, 'TITLE' | 'IN' | 'INC' | 'ATC' | 'AT' | 'ATC_C' | 'AT_C' | 'PE' | 'AE' | 'GOV' | 'ATCS'>;

const OVER_VIEW_LIMIT_ERROR_CODE = 11160405;
const SUPPORTED_TRANS_FIELD: Array<keyof SubsetApiGetPatentBasicResponse> = ['TITLE', 'IN', 'INC', 'ATC', 'AT', 'ATC_C', 'AT_C', 'PE', 'AE', 'GOV', 'ATCS'];

@injectable()
export class GetBasicByIdDTOImpl implements GetBasicByIdDTO {
    constructor(
        @inject(GetFullParamsService)
        private _getFullParamsService: GetFullParamsService,
        @inject(GetPatentBasic)
        private _getPatentBasicApi: GetPatentBasic,
        @inject(GetPatentInitTab)
        private _getPatentInitTabApi: GetPatentInitTab,
        @inject(GetPatentLinkDTO)
        private _getPatentLinkDTO: GetPatentLinkDTO,
        @inject(GetPatentValuationDTO)
        private _getPatentValuationDTO: GetPatentValuationDTO,
        @inject(GetPatentTransferDTO)
        private _getPatentTransferDTO: GetPatentTransferDTO,
        @inject(GetPatentJpdcDTO)
        private _getPatentJpdcDTO: GetPatentJpdcDTO,
    ) {
    }

    private _filterEmptyData(originData: ApiGetPatentBasicResponse) {
        const data = _.cloneDeep(originData);

        _.forEach(data, function(value, key) {
            if (_.isArray(value) && !value.length) {
                delete data[key];
            }
        });

        return data;
    }

    private _formatMultilingualField(data: ApiGetPatentBasicResponse) {
        const _data: PatentOriginExtend = {};

        SUPPORTED_TRANS_FIELD.forEach(field => {
            const {
                value,
                lang,
            } = getFieldValueByLang(data[field], data.LANG_DEFAULT);

            if (lang && value) {
                const key: keyof PatentOriginExtend = `Origin_${field}`;
                (_data[key] as PatentBasicEntity[keyof PatentOriginExtend]) = value;
                _data[`Origin_${field}_Lang`] = lang;
            }
        });

        return _data;
    }

    private async _formatBasicData(basicData: ApiGetPatentBasicResponse): Promise<Omit<PatentBasicEntity, keyof PatentDataStatusExtend>> {
        // 过滤空数组，不展示数据
        const filteredBasicData = this._filterEmptyData(basicData);

        // 翻译字段适配
        const formattedMultilingualField = this._formatMultilingualField(basicData);

        // 生成外部链接
        const linksFiltered = await this._getPatentLinkDTO.getPatentLink({
            docdb: _.get(basicData, 'ORIGINAL_PN.docdb', ''),
            pn: basicData.PN,
            apn: basicData.APN,
            pbd: basicData.PBD,
            type: basicData.TYPE,
            familyId: basicData.FAMILY_ID,
        });

        // 生成专利价值
        const valuation = this._getPatentValuationDTO.getPatentValuation(basicData.valuation, basicData.TYPE);

        // 生成转移信息
        const transfer = this._getPatentTransferDTO.getPatentTransfer(basicData.TRANSFER);

        // 生成JPDC信息
        const jpdc = this._getPatentJpdcDTO.getPatentJpdc(basicData.JPDC_DTERM);

        return {
            ...filteredBasicData,
            ...formattedMultilingualField,
            ...linksFiltered,
            ...valuation,
            ...transfer,
            ...jpdc,
        };
    }

    public async getBasicById(patentId: string): Promise<PatentBasicEntity> {
        try {
            const { data, params } = await this._getFullParamsService.getFullParams(patentId);

            const [basicInfo, tabInfo] = await Promise.all([
                this._getPatentBasicApi.getPatentBasic({ patentId, params, data }),
                this._getPatentInitTabApi.getPatentInitTab(patentId),
            ]);

            const patentEntity = await this._formatBasicData(basicInfo as ApiGetPatentBasicResponse);

            return {
                ...patentEntity,
                ...tabInfo,
            };
        } catch (error) {
            if (error instanceof PsHttpError && error.cause.error_code === OVER_VIEW_LIMIT_ERROR_CODE) {
                throw new Error(OVER_VIEW_LIMIT_ERROR_MESSAGE);
            }
            return DEFAULT_PATENT_BASIC_DATA;
        }
    }
}
